import React from 'react';
import PropTypes from 'prop-types';
import './index.scss';

import Dropzone from 'react-dropzone';

import {formPropTypes} from 'common/components/form/utils';
import {addQueryParams} from 'common/utils/url-util';
import Button from 'common/components/button';
import Modal from 'common/components/modal';
import ModalBody from 'common/components/modal-body';
import ModalFooter from 'common/components/modal-footer';
import ModalHeader from 'common/components/modal-header';
import Spinner from 'common/components/spinner';
import {t} from 'common/utils/translation-util';

class UploadModal extends React.Component {
  static propTypes = {
    accept: PropTypes.array,
    downloadUrl: PropTypes.string.isRequired,
    header: PropTypes.any.isRequired,
    helptext: PropTypes.object,
    onSaveAction: PropTypes.func,
    onSaveActionError: PropTypes.func,
    redirectUrl: PropTypes.string,
    saveButtonProps: PropTypes.object,
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        subtext: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        isDownloadStep: PropTypes.bool,
      }),
    ),
    submitUrl: PropTypes.string.isRequired,
    ...formPropTypes,
  };

  static defaultProps = {
    accept: ['text/csv'],
    isLoading: false,
    saveButtonProps: {
      children: t('Save'),
      isDisabled: true,
      type: 'green',
    },
    steps: [
      {
        isDownloadStep: true,
        title: t('Download CSV template'),
      },
      {
        title: t('Complete your CSV template'),
      },
      {
        title: t('Upload completed CSV template'),
      },
    ],
  };

  constructor(props) {
    super(props);

    this.state = {
      files: [],
      modalIsOpen: false,
    };

    this.onCancel = this.onCancel.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onRemoveFile = this.onRemoveFile.bind(this);
    this.onSave = this.onSave.bind(this);
    this.addSelectUrlParams = this.addSelectUrlParams.bind(this);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.reroute = this.reroute.bind(this);
  }

  getCustomizeParams() {
    return this.props.form?.data ?? {};
  }

  addSelectUrlParams(url) {
    const params = this.getCustomizeParams();
    return addQueryParams(url, params);
  }

  show() {
    this.setState({
      modalIsOpen: true,
    });
  }

  hide() {
    this.setState({
      modalIsOpen: false,
    });
  }

  reroute() {
    this.props.onSaveAction();
  }

  rerouteError() {
    this.props.onSaveActionError();
  }

  renderDownloadButton() {
    const downloadUrl = this.addSelectUrlParams(this.props.downloadUrl);
    const params = this.getCustomizeParams();
    const isDisabled = Object.values(params).some(
      (x) => x === null || x === '',
    );
    return (
      <Button href={downloadUrl} isDisabled={isDisabled} type={'blue'}>
        {t('Download CSV')}
      </Button>
    );
  }

  render() {
    // disable submit button if no files have been attached
    const saveButtonProps = {
      ...this.props.saveButtonProps,
      ...{isDisabled: !this.state.files.length},
    };

    const downloadButton = this.renderDownloadButton();

    const {isLoading, error} = this.state;

    return (
      <Modal className={'UploadModal'} isOpen={this.state.modalIsOpen}>
        <ModalHeader>
          <span>{this.props.header}</span>
          <button
            className={'UploadModal--close'}
            onClick={this.onCancel}
            type={'button'}>
            {'×'}
          </button>
        </ModalHeader>

        <ModalBody>
          {isLoading && <Spinner />}

          {this.props.steps.map((step, idx) => (
            <div className={'UploadModal--step'} key={step.title}>
              <div className={'UploadModal--step-title'}>
                {t(`Step ${idx + 1}: ${step.title || ''}`)}
              </div>

              <div className={'UploadModal--step-subtext'}>
                {step.subtext || ''}
              </div>
              {step.contents}
              {step.isDownloadStep && <p>{downloadButton}</p>}
            </div>
          ))}

          <hr />

          {error && (
            <div className={'UploadModal--error'}>
              {error} &nbsp;
              <span>
                {t('For questions, please contact ')}
                {/* FIXME - use variable -- add to window.settings? */}
                <a href='mailto:support@atrius.com'>{t('Customer Support')}</a>
                {'.'}
              </span>
            </div>
          )}

          <Dropzone
            accept={this.props.accept.join(',')}
            acceptClassName={'Dropzone--accept'}
            className={'Dropzone'}
            multiple={false}
            onDrop={this.onDrop}
            rejectClassName={'Dropzone--reject'}>
            <div className={'upload-instructions'}>
              {t('Drag and drop .csv file here, or ')}
            </div>
            <Button type={'blue'}>{t('Browse for CSV')}</Button>
          </Dropzone>

          <div className={'UploadModal--preview'}>
            {this.state.files.map((file) => (
              <div key={file.name}>
                <span>
                  <i className={'fa-file-alt'} />
                  {`${file.name} - ${file.size} bytes`}
                </span>
                <button
                  className={'UploadModal--close'}
                  onClick={this.onRemoveFile}
                  type={'button'}>
                  {'×'}
                </button>
              </div>
            ))}
          </div>

          {this.props.helptext && (
            <>
              <hr />
              <div className={'UploadModal--step'}>
                <div className={'UploadModal--step-title'}>
                  {t('Helpful Tips:')}
                </div>
                <div className={'UploadModal--step-subtext'}>
                  {this.props.helptext}
                </div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter
          onCancel={this.onCancel}
          onSave={this.onSave}
          saveButtonProps={saveButtonProps}
        />
      </Modal>
    );
  }

  onCancel() {
    this.setState({
      files: [],
      modalIsOpen: false,
    });
  }

  onDrop(acceptedFiles, rejectedFiles) {
    // FIXME
    // This is a workaround to mismatched mime/types between operating systems
    // See https://github.com/react-dropzone/react-dropzone/issues/276
    // Accept all CSV files wholesale
    const files = Array.from(acceptedFiles);
    rejectedFiles.forEach((file) => {
      if (file.name.endsWith('.csv')) {
        files.push(file);
      }
    });
    if (files.length > 0) {
      this.setState({
        error: null,
        files,
      });
    }
  }

  onRemoveFile(index) {
    const files = [].concat(this.state.files);
    files.splice(index, 1);
    this.setState({
      error: null,
      files,
    });
  }

  onSave() {
    const data = this.props.form?.data;
    const {files} = this.state;

    if (!files) return;

    this.setState({isLoading: true});

    // reroute this modal to an alternate action,
    // but continue the upload process
    if (this.props.onSaveAction) {
      this.reroute();
    }

    const formData = new FormData();
    if (data) {
      for (const [name, value] of Object.entries(data)) {
        formData.append(name, value);
      }
    }
    for (const file of files) {
      formData.append('file', file, file.name);
    }
    formData.append('csrfmiddlewaretoken', window.settings.csrf);

    return fetch(this.props.submitUrl, {
      method: 'POST',
      body: formData,
    }).then((response) => {
      if (response.ok) {
        // successful response returns url as text
        if (!this.props.onSaveAction && this.props.redirectUrl) {
          location.assign(this.props.redirectUrl);
        } else {
          this.setState({
            files: [],
            isLoading: false,
            modalIsOpen: false,
          });
        }
      } else {
        if (this.props.onSaveActionError) {
          this.rerouteError();
        }
        this.setState({
          error: t('Failed to upload data.'),
          isLoading: false,
        });
      }
    });
  }
}

export default UploadModal;
